import { createContext, useContext, useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { AuthContextParams, AuthProviderProps, UserBranchID, UserDetails } from "./types";
import { useGetUserBranchLazyQuery } from "../graphql";
import { apiTokenRequest, loginRequest } from "../utils/msalConfig";
import { getLandingPath, Idp, UNAUTHORIZED_PATH } from "../utils/authorization";

/**
 * Shared, IdP-agnostic access token holder. Populated by whichever auth provider
 * is active (Okta or Entra) and read synchronously by the Apollo auth link.
 */
export const authTokenRef: { current: string | null } = { current: null };

const AuthContext = createContext<AuthContextParams>({
  userInfo: undefined,
  userBranchId: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
});

/**
 * Decodes the payload claims of a JWT without verifying the signature.
 * Signature verification happens at the gateway/API — the SPA only needs
 * the claims for display and routing.
 */
const decodeJwtClaims = (token: string): Record<string, unknown> => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return {};
  }
};

const extractGroupsFromClaims = (
  ...claimSources: Array<Record<string, unknown> | undefined>
): string[] => {
  for (const claims of claimSources) {
    if (!claims) continue;
    const groups = claims.groups;
    if (Array.isArray(groups)) {
      return groups.filter((group): group is string => typeof group === "string");
    }
  }
  return [];
};

/**
 * Shared logic used by both IdP providers: looks up the user's branch by email
 * and performs the first-login, role-based redirect.
 */
const useUserBranchAndRedirect = (
  userInfo: UserDetails | undefined,
  idp: Idp
): UserBranchID => {
  const history = useHistory();
  const [userBranchId, setUserBranchId] = useState<UserBranchID>();
  const [userBranch] = useGetUserBranchLazyQuery();

  useEffect(() => {
    if (userInfo?.email) {
      const variables = { input: userInfo.email.toLowerCase() };
      userBranch({ variables })
        .then(({ data }) => {
          if (data) {
            setUserBranchId(data.userBranch.branch);
          } else {
            throw new Error();
          }
        })
        .catch(() => {
          setUserBranchId(null);
        });
    }

    if (userInfo && window.location.pathname === "/") {
      const landingPath = getLandingPath(userInfo.groups, idp) ?? UNAUTHORIZED_PATH;
      history.replace(toRelativeUrl(landingPath, window.location.origin));
    }
  }, [userInfo]);

  return userBranchId;
};

/**
 * Okta authentication provider. Reads auth state and user info from the Okta
 * SDK and publishes them through the shared AuthContext.
 */
export const OktaAuthProvider = ({ children }: AuthProviderProps) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState<UserDetails>();
  const history = useHistory();

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      setUserInfo(undefined);
      authTokenRef.current = null;
    } else {
      const accessToken = oktaAuth.getAccessToken();
      authTokenRef.current = accessToken ?? null;
      oktaAuth
        .getUser()
        .then((info) => setUserInfo(info as UserDetails))
        .catch((err) => console.error(err));
    }
  }, [authState, oktaAuth]);

  const userBranchId = useUserBranchAndRedirect(userInfo, "okta");

  return (
    <AuthContext.Provider
      value={{
        userInfo,
        userBranchId,
        isAuthenticated: !!authState?.isAuthenticated,
        login: () => {
          oktaAuth.signInWithRedirect();
        },
        logout: () => {
          authTokenRef.current = null;
          history.replace("/");
          oktaAuth.signOut({
            postLogoutRedirectUri: `${window.location.origin}/`,
          });
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Entra ID authentication provider. Acquires an access token via MSAL,
 * extracts identity claims, and publishes them through the shared AuthContext.
 */
export const EntraAuthProvider = ({ children }: AuthProviderProps) => {
  const { instance, accounts } = useMsal();
  const [userInfo, setUserInfo] = useState<UserDetails>();

  useEffect(() => {
    if (accounts.length === 0) {
      setUserInfo(undefined);
      authTokenRef.current = null;
      return;
    }

    const account = accounts[0];
    instance
      .acquireTokenSilent({ ...apiTokenRequest, account })
      .then((result) => {
        authTokenRef.current = result.accessToken;
        const accessClaims = decodeJwtClaims(result.accessToken);
        const idClaims = account.idTokenClaims as Record<string, unknown> | undefined;
        setUserInfo({
          name: account.name ?? (accessClaims.name as string | undefined),
          email:
            (accessClaims.preferred_username as string) ??
            (accessClaims.email as string) ??
            (idClaims?.preferred_username as string) ??
            (idClaims?.email as string) ??
            account.username,
          groups: extractGroupsFromClaims(accessClaims, idClaims),
        });
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({ ...apiTokenRequest, account });
        } else {
          console.error(error);
        }
      });
  }, [instance, accounts]);

  const userBranchId = useUserBranchAndRedirect(userInfo, "entra");

  return (
    <AuthContext.Provider
      value={{
        userInfo,
        userBranchId,
        isAuthenticated: accounts.length > 0,
        login: () => {
          instance.loginRedirect(loginRequest);
        },
        logout: () => {
          authTokenRef.current = null;
          instance.logoutRedirect({
            postLogoutRedirectUri: `${window.location.origin}/`,
          });
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
